import React, { useState, useEffect, useRef, useCallback } from 'react';
import '@/App.css';
import { MessageSquare, Phone, Mail, MapPin, ArrowRight, Award, CheckCircle, Clock, Lightbulb, DollarSign, Users, Menu, X, Star } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

const WHATSAPP_NUMBER = '919677063222';
const WHATSAPP_MESSAGE = encodeURIComponent('Hi GKVR Promoters, I am interested in your plots. Please share details.');
const LOGO_URL = 'assets/images/logo.png';
const HERO_BG_URL = 'assets/images/hero-bg.jpg';
const ABOUT_US_URL = 'assets/images/about-us.avif';
const LEADS_STORAGE_KEY = 'gkvr_leads_static';

const getLeadsAdminRouteStatus = () => window.location.hash === '#/leads-admin';

function App() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    plotSize: '',
    hearAbout: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLeadsAdminPage, setIsLeadsAdminPage] = useState(() => getLeadsAdminRouteStatus());
  const [leads, setLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [leadsError, setLeadsError] = useState('');
  const logoImageRef = useRef(null);
  const logoWrapperRef = useRef(null);
  
  // Slideshow state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  
  // Spring County project images - 2 unique photos
  const springCountyImages = [
    'assets/images/spring-county-entrance.png',
    'assets/images/spring-county-layout.jpg'
  ];

  // Scroll animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const fadeElements = document.querySelectorAll('.fade-up');
    fadeElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (logoImageRef.current) {
      logoImageRef.current.setAttribute(
        'style',
        'height:150px !important; width:auto !important; display:block !important; object-fit:contain !important;'
      );
    }

    if (logoWrapperRef.current) {
      logoWrapperRef.current.setAttribute(
        'style',
        'display:flex !important; align-items:center !important; padding:8px 0px !important;'
      );
    }
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      setIsLeadsAdminPage(getLeadsAdminRouteStatus());
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const fetchLeads = useCallback(async () => {
    setIsLoadingLeads(true);
    setLeadsError('');
    try {
      const rawLeads = window.localStorage.getItem(LEADS_STORAGE_KEY);
      const parsedLeads = rawLeads ? JSON.parse(rawLeads) : [];
      setLeads(parsedLeads);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLeadsError('Unable to load leads. Please refresh.');
      toast.error('Failed to load leads.');
    } finally {
      setIsLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    if (isLeadsAdminPage) {
      fetchLeads();
    }
  }, [isLeadsAdminPage, fetchLeads]);

  const openLeadsAdminPage = () => {
    window.location.hash = '/leads-admin';
    setIsLeadsAdminPage(true);
    window.scrollTo(0, 0);
  };

  const openMainWebsitePage = () => {
    window.location.hash = '/';
    setIsLeadsAdminPage(false);
    window.scrollTo(0, 0);
  };

  const formatLeadDateTime = (timestamp) => {
    const parsedDate = new Date(timestamp);
    if (Number.isNaN(parsedDate.getTime())) {
      return timestamp;
    }

    return parsedDate.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Slideshow auto-advance
  useEffect(() => {
    if (!isHovering) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % springCountyImages.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isHovering, springCountyImages.length]);
  
  // Navigate slides
  const nextSlide = (e) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev + 1) % springCountyImages.length);
  };
  
  const prevSlide = (e) => {
    e.stopPropagation();
    setCurrentSlide((prev) => (prev - 1 + springCountyImages.length) % springCountyImages.length);
  };
  
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = {
        id: window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        name: formData.name,
        phone: formData.phone,
        city: formData.city,
        message: `Plot Size: ${formData.plotSize} | Heard from: ${formData.hearAbout} | Message: ${formData.message}`,
        timestamp: new Date().toISOString()
      };

      const existingLeadsRaw = window.localStorage.getItem(LEADS_STORAGE_KEY);
      const existingLeads = existingLeadsRaw ? JSON.parse(existingLeadsRaw) : [];
      const updatedLeads = [submitData, ...existingLeads];
      window.localStorage.setItem(LEADS_STORAGE_KEY, JSON.stringify(updatedLeads));

      toast.success('Thank you! We will contact you soon.');
      setFormData({ name: '', phone: '', city: '', plotSize: '', hearAbout: '', message: '' });

      if (isLeadsAdminPage) {
        setLeads(updatedLeads);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const navLinks = [
    { number: '01', name: 'Home', href: '#home' },
    { number: '02', name: 'About Us', href: '#about' },
    { number: '03', name: 'Projects', href: '#projects' },
    { number: '04', name: 'Why Us', href: '#why-choose' },
    { number: '05', name: 'Testimonials', href: '#testimonials' },
    { number: '06', name: 'Contact', href: '#contact' }
  ];

  const features = [
    {
      icon: <Award className="w-10 h-10" />,
      title: 'DTCP & RERA Approved',
      description: 'All plots come with government approvals and certifications'
    },
    {
      icon: <CheckCircle className="w-10 h-10" />,
      title: 'Clear Legal Title',
      description: 'Zero complications with complete documentation support'
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: 'On-Time Delivery',
      description: 'We honor our commitments and deliver as promised'
    },
    {
      icon: <Lightbulb className="w-10 h-10" />,
      title: 'Roads Water & Electricity',
      description: 'Complete infrastructure ready before handover'
    },
    {
      icon: <DollarSign className="w-10 h-10" />,
      title: 'Transparent Pricing',
      description: 'No hidden costs - what you see is what you pay'
    },
    {
      icon: <Users className="w-10 h-10" />,
      title: 'Trusted by 100+ Families',
      description: 'Building dreams and delivering trust since inception'
    }
  ];

  const testimonials = [
    {
      name: 'Rajesh Kumar',
      city: 'Chennai',
      text: 'GKVR Promoters delivered exactly what they promised. Clear documentation, great location, and zero hassles. Highly recommended!',
      rating: 5
    },
    {
      name: 'Priya Sharma',
      city: 'Coimbatore',
      text: 'As an NRI investor, I was worried about the process. GKVR made it incredibly smooth with complete transparency and regular updates.',
      rating: 5
    },
    {
      name: 'Arun Venkatesh',
      city: 'Madurai',
      text: 'Best decision we made for our family. The plot came with all approvals and infrastructure. Thank you GKVR team!',
      rating: 5
    }
  ];

  const serviceStripItems = [
    'Land Aggregation',
    'Approved Plots',
    'Property Development and Management',
    'Quality Home Construction',
    'Interior and Exterior Works'
  ];

  const aboutServices = [
    {
      number: '01',
      title: 'Land Aggregation',
      description:
        "At GKVR Promoters, land aggregation is where everything begins. We meticulously identify and secure large, strategically located land parcels in Tamil Nadu's fastest-growing corridors — areas with proven infrastructure momentum, proximity to industrial hubs, and strong long-term appreciation potential. Every parcel undergoes rigorous due diligence covering legal title verification, soil assessment, government zoning compliance and connectivity analysis before a single rupee is committed. This disciplined, research-driven approach ensures that by the time a plot reaches your hands, it already carries the foundation of a sound, high-value investment."
    },
    {
      number: '02',
      title: 'Approved Plots',
      description:
        'We obtain full DTCP and RERA government approvals before selling — so every plot you buy is 100% legally secure with zero complications.'
    },
    {
      number: '03',
      title: 'Property Development and Management',
      description:
        'From raw land to a fully developed layout — we build roads, install water supply, drainage and electricity, and manage the entire project so your investment is ready to grow.'
    },
    {
      number: '04',
      title: 'Quality Home Construction',
      description:
        'Our experienced construction team builds your dream home to the highest standards — on time, within budget and built to last generations.'
    },
    {
      number: '05',
      title: 'Interior and Exterior Works',
      description:
        'From beautifully designed interiors to striking facade finishes — our design team creates spaces that are functional, elegant and a true reflection of your personality.'
    }
  ];

  if (isLeadsAdminPage) {
    return (
      <div className="min-h-screen bg-white page-fade-in" data-testid="leads-admin-page">
        <Toaster position="top-center" richColors />

        <header className="sticky top-0 z-50 bg-white shadow-sm" style={{ borderBottom: '1px solid #E0DDD8', padding: '10px 0' }}>
          <div className="max-w-7xl mx-auto px-8 md:px-16 flex justify-between items-center">
            <div ref={logoWrapperRef} data-testid="header-logo-wrapper">
              <img
                src={LOGO_URL}
                alt="GKVR Promoters Logo"
                ref={logoImageRef}
                data-testid="header-logo"
              />
            </div>
            <button
              onClick={openMainWebsitePage}
              className="luxury-button luxury-button-outline-dark"
              style={{ padding: '10px 18px', fontSize: '10px', letterSpacing: '1.5px' }}
              data-testid="leads-admin-back-button"
            >
              Back to Website
            </button>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-6 md:px-12 py-16" data-testid="leads-admin-content">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
            <div>
              <h1
                style={{
                  fontFamily: 'Playfair Display, serif',
                  fontSize: '44px',
                  fontWeight: '400',
                  color: '#1A1A1A',
                  marginBottom: '12px'
                }}
                data-testid="leads-admin-title"
              >
                Leads Admin
              </h1>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#666666' }} data-testid="leads-admin-subtitle">
                Total Captured Leads: <span style={{ color: '#F5A623', fontWeight: '600' }} data-testid="leads-admin-total-count">{leads.length}</span>
              </p>
            </div>
            <button
              onClick={fetchLeads}
              className="luxury-button luxury-button-outline-dark"
              style={{ padding: '12px 22px' }}
              data-testid="leads-admin-refresh-button"
            >
              Refresh Leads
            </button>
          </div>

          {isLoadingLeads && (
            <div className="py-10 text-center" data-testid="leads-admin-loading">
              <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#666666' }}>Loading leads...</p>
            </div>
          )}

          {!isLoadingLeads && leadsError && (
            <div className="py-10 text-center" data-testid="leads-admin-error">
              <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#B42318' }}>{leadsError}</p>
            </div>
          )}

          {!isLoadingLeads && !leadsError && leads.length === 0 && (
            <div className="py-10 text-center" data-testid="leads-admin-empty-state">
              <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#666666' }}>No leads captured yet.</p>
            </div>
          )}

          {!isLoadingLeads && !leadsError && leads.length > 0 && (
            <div className="border border-[#E0DDD8] overflow-x-auto" data-testid="leads-admin-table-wrapper">
              <table className="w-full min-w-[920px]" data-testid="leads-admin-table">
                <thead style={{ background: '#F8F7F4' }}>
                  <tr>
                    <th className="text-left px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666666' }}>Name</th>
                    <th className="text-left px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666666' }}>Phone</th>
                    <th className="text-left px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666666' }}>City</th>
                    <th className="text-left px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666666' }}>Message</th>
                    <th className="text-left px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#666666' }}>Submitted At</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead, index) => (
                    <tr key={lead.id} style={{ borderTop: '1px solid #EDEAE6' }} data-testid={`leads-admin-row-${index}`}>
                      <td className="px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', color: '#1A1A1A', fontSize: '14px' }} data-testid={`leads-admin-name-${index}`}>{lead.name}</td>
                      <td className="px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', color: '#1A1A1A', fontSize: '14px' }} data-testid={`leads-admin-phone-${index}`}>{lead.phone}</td>
                      <td className="px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', color: '#1A1A1A', fontSize: '14px' }} data-testid={`leads-admin-city-${index}`}>{lead.city}</td>
                      <td className="px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', color: '#444444', fontSize: '14px', minWidth: '260px' }} data-testid={`leads-admin-message-${index}`}>{lead.message}</td>
                      <td className="px-5 py-4" style={{ fontFamily: 'DM Sans, sans-serif', color: '#666666', fontSize: '13px', whiteSpace: 'nowrap' }} data-testid={`leads-admin-time-${index}`}>{formatLeadDateTime(lead.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white page-fade-in">
      <Toaster position="top-center" richColors />
      
      {/* Avantgarde Header - Pure White */}
      <header className="sticky top-0 z-50 bg-white shadow-sm" style={{ borderBottom: '1px solid #E0DDD8', padding: '10px 0' }}>
        <div className="max-w-7xl mx-auto px-8 md:px-16 flex justify-between items-center">
          <div ref={logoWrapperRef} data-testid="header-logo-wrapper">
            <img 
              src={LOGO_URL} 
              alt="GKVR Promoters Logo" 
              ref={logoImageRef}
              data-testid="header-logo"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex gap-6 items-center">
            {navLinks.map((link) => (
              <a
                key={link.number}
                href={link.href}
                className="flex flex-col items-center nav-link-avantgarde"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '11px',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                  fontWeight: '500',
                  color: '#1A1A1A',
                  whiteSpace: 'nowrap',
                  marginRight: link.name === 'Contact' ? '24px' : '0'
                }}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                <span style={{ fontSize: '8px', color: '#F5A623', marginBottom: '4px' }}>{link.number}</span>
                {link.name}
              </a>
            ))}
          </nav>

          {/* Desktop Book Visit Button */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block luxury-button luxury-button-outline-dark"
            style={{
              padding: '10px 18px',
              fontSize: '10px',
              letterSpacing: '1.5px',
              whiteSpace: 'nowrap'
            }}
            data-testid="header-whatsapp-btn"
          >
            Book Site Visit
          </a>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-[#1C2B3A] z-50"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-[#E0DDD8]">
            <nav className="flex flex-col py-6">
              {navLinks.map((link) => (
                <a
                  key={link.number}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-8 py-4 hover:bg-gray-50 transition-colors"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '12px',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    fontWeight: '500',
                    color: '#1A1A1A'
                  }}
                >
                  <span style={{ fontSize: '9px', color: '#F5A623', marginRight: '8px' }}>{link.number}</span>
                  {link.name}
                </a>
              ))}
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mx-8 mt-4 luxury-button luxury-button-outline-dark text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book Site Visit
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section - Full Screen with Ken Burns */}
      <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 ken-burns-bg"
          style={{
            backgroundImage: `url('${HERO_BG_URL}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0" style={{ background: 'rgba(0, 0, 0, 0.38)' }} />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h1 
            className="hero-headline"
            style={{
              fontFamily: 'Playfair Display, serif',
              fontSize: '68px',
              fontWeight: '400',
              color: '#ffffff',
              lineHeight: '1.2',
              marginBottom: '0'
            }}
          >
            Building Dreams,<br />Delivering Trust
          </h1>

          <div className="hero-services-row" data-testid="hero-services-row">
            {serviceStripItems.map((item, index) => (
              <React.Fragment key={`hero-${item}`}>
                <span className="hero-service-item" data-testid={`hero-service-item-${index + 1}`}>
                  {item}
                </span>
                {index < serviceStripItems.length - 1 && (
                  <div className="hero-service-divider" data-testid={`hero-service-divider-${index + 1}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - Pure White */}
      <section id="about" className="py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="fade-up text-center mb-12">
            <div className="decorative-line" />
            <h2 
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '42px',
                fontWeight: '400',
                color: '#1A1A1A',
                marginBottom: '32px'
              }}
            >
              About Us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="fade-up stagger-1">
              <div className="about-image-wrapper">
                <img
                  src={ABOUT_US_URL}
                  alt="About GKVR Promoters"
                  className="about-image"
                  data-testid="about-image"
                />
              </div>
            </div>
            <div className="fade-up stagger-2">
              <p 
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '15px',
                  lineHeight: '1.9',
                  color: '#555555',
                  marginBottom: '24px',
                  textAlign: 'left'
                }}
              >
                GKVR Promoters Pvt Ltd is a Tamil Nadu-based private limited real estate development company that acquires large parcels of land, obtains all necessary government approvals including DTCP and RERA certification, develops essential infrastructure such as roads, water supply, drainage, and electricity connections, and divides the land into well-planned residential plots sold to local families, working professionals, and NRI investors.
              </p>
              <p 
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '15px',
                  lineHeight: '1.9',
                  color: '#555555',
                  textAlign: 'left'
                }}
              >
                Every plot comes with a clear legal title, zero complications, and full documentation support.
              </p>
            </div>
          </div>

          <div className="mt-20" data-testid="about-services-section">
            <div className="about-services-decorative-line" data-testid="about-services-decorative-line" />
            <h3 className="about-services-title" data-testid="about-services-title">
              Our Services
            </h3>

            <div className="about-services-list" data-testid="about-services-list">
              {aboutServices.map((service, index) => (
                <div
                  key={service.number}
                  className={`about-service-block fade-up about-service-stagger-${index + 1}`}
                  data-testid={`about-service-block-${index + 1}`}
                >
                  <div className="about-service-number" data-testid={`about-service-number-${index + 1}`}>
                    {service.number}
                  </div>
                  <div className="about-service-content" data-testid={`about-service-content-${index + 1}`}>
                    <h4 className="about-service-title" data-testid={`about-service-title-${index + 1}`}>
                      {service.title}
                    </h4>
                    <p className="about-service-description" data-testid={`about-service-description-${index + 1}`}>
                      {service.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section - Advanced with Slideshow */}
      <section id="projects" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="fade-up text-center mb-16">
            <div className="decorative-line" />
            <h2 
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '42px',
                fontWeight: '400',
                color: '#1A1A1A',
                marginBottom: '12px'
              }}
            >
              Our Projects
            </h2>
            <p 
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '16px',
                fontStyle: 'italic',
                color: '#666666'
              }}
            >
              Carefully planned layouts built for your family's future
            </p>
          </div>
          
          {/* Spring County Project Card */}
          <div 
            className="fade-up project-card bg-white overflow-hidden"
            style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
          >
            <div className="grid md:grid-cols-[60%_40%]">
              {/* Left Side - Photo Slideshow */}
              <div 
                className="slideshow-container"
                style={{ height: '600px' }}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {springCountyImages.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Spring County ${index + 1}`}
                    className={`slideshow-image ${index === currentSlide ? 'active' : ''}`}
                  />
                ))}
                
                {/* Arrow Navigation */}
                <button className="slideshow-arrow left" onClick={prevSlide}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <button className="slideshow-arrow right" onClick={nextSlide}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
                
                {/* Dot Indicators */}
                <div className="slideshow-dots">
                  {springCountyImages.map((_, index) => (
                    <button
                      key={index}
                      className={`slideshow-dot ${index === currentSlide ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToSlide(index);
                      }}
                    />
                  ))}
                </div>
              </div>
              
              {/* Right Side - Project Details Panel */}
              <div 
                style={{ 
                  background: '#1C2B3A',
                  padding: '36px',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '600px'
                }}
              >
                {/* Featured Project Label */}
                <p 
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '10px',
                    letterSpacing: '3px',
                    textTransform: 'uppercase',
                    color: '#F5A623',
                    marginBottom: '8px'
                  }}
                >
                  Featured Project
                </p>
                
                {/* Project Name */}
                <h3 
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '32px',
                    fontWeight: '700',
                    color: 'white',
                    marginBottom: '12px'
                  }}
                >
                  SPRING COUNTY
                </h3>
                
                {/* Location */}
                <p 
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '12px',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: '#F5A623',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <MapPin className="w-4 h-4" />
                  Sriperumbudur, Sunguvarchatram
                </p>
                
                {/* Divider */}
                <div style={{ width: '100%', height: '1px', background: '#F5A623', marginBottom: '16px' }} />
                
                {/* Plot Sizes */}
                <p 
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '11px',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: 'white',
                    marginBottom: '8px'
                  }}
                >
                  Plot Sizes
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                  {['1500 Sq.ft', '1800 Sq.ft', '2000 Sq.ft', '2400 Sq.ft', '3000 Sq.ft'].map((size) => (
                    <span key={size} className="plot-size-pill">{size}</span>
                  ))}
                </div>
                
                {/* Price */}
                <p 
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '22px',
                    color: '#F5A623',
                    marginBottom: '16px'
                  }}
                >
                  Starting at Rs 1,600 per Sq.ft
                </p>
                
                {/* Divider */}
                <div style={{ width: '100%', height: '1px', background: '#F5A623', marginBottom: '16px' }} />
                
                {/* Highlights */}
                <p 
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    fontSize: '10px',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    color: 'white',
                    marginBottom: '10px'
                  }}
                >
                  Highlights
                </p>
                <div style={{ marginBottom: '16px' }}>
                  {[
                    '100% DTCP Approved Layout',
                    'Facing Bangalore Express Highway',
                    'Just 100m from New Bangalore Express Highway',
                    '600m from Thiruvallur High Road',
                    'Near Sunguvarchatram Junction',
                    'Close to Registrar Office',
                    'Easy access to Colleges and Industries',
                    'Just 7km before Parandur Airport — High Appreciation Area'
                  ].map((highlight, index) => (
                    <div key={index} className="highlight-item">
                      <span className="highlight-icon">✦</span>
                      <span>{highlight}</span>
                    </div>
                  ))}
                </div>
                
                {/* Divider */}
                <div style={{ width: '100%', height: '1px', background: '#F5A623', marginTop: 'auto', marginBottom: '20px' }} />
                
                {/* Buttons */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <button
                    onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      background: '#F5A623',
                      color: '#1C2B3A',
                      border: 'none',
                      padding: '12px 16px',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '11px',
                      fontWeight: '600',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#d48a1a'}
                    onMouseLeave={(e) => e.target.style.background = '#F5A623'}
                  >
                    Enquire Now
                  </button>
                  <a
                    href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi GKVR Promoters, I am interested in the Spring County project at Sriperumbudur Sunguvarchatram. Please share details and brochure.')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: 'transparent',
                      color: 'white',
                      border: '1px solid white',
                      padding: '12px 16px',
                      fontFamily: 'DM Sans, sans-serif',
                      fontSize: '11px',
                      fontWeight: '600',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      textAlign: 'center',
                      textDecoration: 'none',
                      display: 'block',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'white';
                      e.target.style.color = '#1C2B3A';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = 'white';
                    }}
                  >
                    Book Site Visit
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Choose Us - Light Warm Grey */}
      <section id="why-choose" className="py-32" style={{ backgroundColor: '#F5F3F0' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="fade-up text-center mb-16">
            <div className="decorative-line" />
            <h2 
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '42px',
                fontWeight: '400',
                color: '#1A1A1A',
                marginBottom: '16px'
              }}
            >
              Why Choose Us
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#666666' }}>
              Six reasons to trust GKVR Promoters with your investment
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`fade-up stagger-${index + 1} luxury-card bg-white p-8`}
                style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}
                data-testid={`feature-card-${index}`}
              >
                <div style={{ color: '#F5A623', marginBottom: '16px' }}>
                  {feature.icon}
                </div>
                <h4 
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '20px',
                    fontWeight: '400',
                    color: '#1A1A1A',
                    marginBottom: '12px'
                  }}
                >
                  {feature.title}
                </h4>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#666666', lineHeight: '1.7' }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - Pure White */}
      <section id="testimonials" className="py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="fade-up text-center mb-16">
            <div className="decorative-line" />
            <h2 
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '42px',
                fontWeight: '400',
                color: '#1A1A1A',
                marginBottom: '16px'
              }}
            >
              What Our Clients Say
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#666666' }}>
              Building trust, one family at a time
            </p>
          </div>
          
          <div className="space-y-12">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`fade-up stagger-${index + 1} relative py-8`}
                style={{ borderBottom: index < testimonials.length - 1 ? '1px solid #E0DDD8' : 'none' }}
                data-testid={`testimonial-card-${index}`}
              >
                <div className="quote-decoration">"</div>
                <p 
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '20px',
                    fontStyle: 'italic',
                    color: '#1A1A1A',
                    lineHeight: '1.7',
                    marginBottom: '24px',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3">
                  <div>
                    <p 
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        fontSize: '12px',
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        fontWeight: '600',
                        color: '#F5A623'
                      }}
                    >
                      {testimonial.name}
                    </p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#666666' }}>
                      {testimonial.city}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section - Pure White, Two Column */}
      <section id="contact" className="py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="fade-up text-center mb-16">
            <div className="decorative-line" />
            <h2 
              style={{
                fontFamily: 'Playfair Display, serif',
                fontSize: '42px',
                fontWeight: '400',
                color: '#1A1A1A',
                marginBottom: '16px'
              }}
            >
              Get In Touch
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#666666' }}>
              Ready to invest in your dream plot? Contact us today
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-16">
            {/* Left Column - Contact Info */}
            <div className="fade-up stagger-1 space-y-8">
              <div>
                <h3 
                  style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '24px',
                    fontWeight: '400',
                    color: '#1A1A1A',
                    marginBottom: '24px'
                  }}
                >
                  GKVR Promoters Pvt Ltd
                </h3>
                <div className="space-y-4">
                  <div>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#666666', marginBottom: '4px' }}>
                      Phone
                    </p>
                    <a 
                      href="tel:+919677063222" 
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#F5A623', fontWeight: '500' }}
                      data-testid="contact-phone"
                    >
                      +91 96770 63222
                    </a>
                  </div>
                  <div>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#666666', marginBottom: '4px' }}>
                      Email
                    </p>
                    <a 
                      href="mailto:gkvrpromoterspvtltd9@gmail.com" 
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#F5A623', fontWeight: '500' }}
                      data-testid="contact-email"
                    >
                      gkvrpromoterspvtltd9@gmail.com
                    </a>
                  </div>
                  <div>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '13px', color: '#666666', marginBottom: '4px' }}>
                      Location
                    </p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '15px', color: '#1A1A1A' }}>
                      Tamil Nadu, India
                    </p>
                  </div>
                  <div className="pt-4">
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="luxury-button inline-flex items-center gap-2"
                      style={{ border: '1px solid #F5A623', color: '#F5A623', background: 'transparent' }}
                      data-testid="contact-whatsapp"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Chat on WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Column - Minimal Form */}
            <div className="fade-up stagger-2">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="minimal-input w-full"
                  data-testid="form-name-input"
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="minimal-input w-full"
                  data-testid="form-phone-input"
                />
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="minimal-input w-full"
                  data-testid="form-city-input"
                />
                <select
                  name="plotSize"
                  value={formData.plotSize}
                  onChange={handleInputChange}
                  required
                  className="minimal-input w-full"
                  data-testid="form-plot-size-select"
                >
                  <option value="">Plot Size Interested In</option>
                  <option value="1200 sqft">1200 sqft</option>
                  <option value="1500 sqft">1500 sqft</option>
                  <option value="1800 sqft">1800 sqft</option>
                  <option value="2400 sqft">2400 sqft</option>
                </select>
                <select
                  name="hearAbout"
                  value={formData.hearAbout}
                  onChange={handleInputChange}
                  required
                  className="minimal-input w-full"
                  data-testid="form-hear-about-select"
                >
                  <option value="">How did you hear about us?</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Instagram">Instagram</option>
                  <option value="WhatsApp">WhatsApp</option>
                  <option value="Friend Referral">Friend Referral</option>
                  <option value="Google">Google</option>
                  <option value="Other">Other</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className="minimal-input w-full resize-none"
                  data-testid="form-message-input"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="luxury-button luxury-button-solid w-full disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="form-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Send Enquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Pure White */}
      <footer className="bg-white py-20" style={{ borderTop: '1px solid #E0DDD8' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src={LOGO_URL} 
                alt="GKVR Promoters Logo" 
                className="h-[45px] w-auto object-contain mb-6"
              />
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#666666', lineHeight: '1.7', marginBottom: '8px' }}>
                Building Dreams, Delivering Trust
              </p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '12px', color: '#999999' }}>
                RERA: TN/RERA/0003/2026
              </p>
            </div>
            <div>
              <h5 
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '13px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  color: '#1A1A1A',
                  marginBottom: '16px'
                }}
              >
                Quick Links
              </h5>
              <ul className="space-y-2">
                {['Home', 'About Us', 'Projects', 'Contact'].map((link) => (
                  <li key={link}>
                    <a 
                      href={`#${link.toLowerCase().replace(' ', '')}`}
                      style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#666666' }}
                      className="hover:text-[#F5A623] transition-colors"
                    >
                      {link}
                    </a>
                  </li>
                ))}
                <li>
                  <button
                    onClick={openLeadsAdminPage}
                    style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#666666', background: 'transparent', border: 'none', padding: 0 }}
                    className="hover:text-[#F5A623] transition-colors"
                    data-testid="footer-leads-admin-button"
                  >
                    Leads Admin
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h5 
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: '13px',
                  letterSpacing: '2px',
                  textTransform: 'uppercase',
                  fontWeight: '600',
                  color: '#1A1A1A',
                  marginBottom: '16px'
                }}
              >
                Working Hours
              </h5>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '14px', color: '#666666', marginBottom: '16px' }}>
                Mon – Sun: 9AM – 6PM
              </p>
              <div className="flex gap-4 mt-4">
                <a 
                  href="https://www.facebook.com/profile.php?id=61584014201324&sk=about" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  data-testid="social-facebook"
                >
                  <svg className="w-5 h-5" style={{ color: '#F5A623' }} fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a 
                  href="https://instagram.com/gkvrpromoters" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  data-testid="social-instagram"
                >
                  <svg className="w-5 h-5" style={{ color: '#F5A623' }} fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/><path d="M12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a 
                  href="https://wa.me/919677063222" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-70 transition-opacity"
                  data-testid="social-whatsapp"
                >
                  <MessageSquare className="w-5 h-5" style={{ color: '#F5A623' }} />
                </a>
              </div>
            </div>
          </div>
          <div className="pt-8" style={{ borderTop: '1px solid #E0DDD8' }}>
            <p 
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '13px',
                color: '#999999',
                textAlign: 'center'
              }}
            >
              © {new Date().getFullYear()} GKVR Promoters Pvt Ltd. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button - Gold */}
      <a
        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
      >
        <MessageSquare className="w-7 h-7 text-white" />
      </a>
    </div>
  );
}

export default App;
